<template>
  <div>
    <setting-layout
      :title="title"
      :subtitle="subtitle"
      :has-permission="false"
      :show-help="false"
      @cancel="cancel"
      @save="save"
    >
      <div class="mt-7">
        <v-alert
          :type="isEnabled ? 'success' : 'warning'"
          outlined
          prominent
          class="mb-6"
        >
          <div class="text-subtitle-1 font-weight-bold">{{ statusTitle }}</div>
          <div class="text-body-2 mt-1">
            {{ isEnabled ? '休診時間可隨時調整。' : '您可以先設定休診時段；客服開通功能後，設定會自動套用。' }}
          </div>
          <v-btn
            v-if="!isEnabled"
            :href="$enum.DENTCO_CS_LINE"
            target="_blank"
            depressed
            color="orange-6"
            dark
            :block="$vuetify.breakpoint.xsOnly"
            class="mt-3"
          >
            聯繫客服申請啟用
            <v-icon small right>mdi-open-in-new</v-icon>
          </v-btn>
        </v-alert>

        <v-card outlined class="rounded-lg">
          <v-card-title class="pa-4 pa-md-6">
            <div>
              <div class="text-h6 font-weight-bold">休診規則</div>
              <div class="text-body-2 font-weight-regular grey-7--text mt-1">
                特定日期的設定優先於週期規則。
              </div>
            </div>
            <v-spacer />
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  small
                  color="grey-7"
                  class="mt-2 mt-sm-0"
                  tabindex="0"
                  :aria-label="`時區：${timezone}`"
                >
                  mdi-earth
                </v-icon>
              </template>
              <span>時區：{{ timezone }}</span>
            </v-tooltip>
          </v-card-title>
          <v-divider />

          <v-card-text class="pa-4 pa-md-6">
            <v-card v-if="!rules.length && !editingIsNew" flat color="grey-2" class="rounded-lg pa-8 text-center">
              <v-icon size="44" color="tiffany-8">mdi-calendar-blank-outline</v-icon>
              <div class="text-subtitle-2 font-weight-bold grey-5--text mt-3">尚未設定休診規則</div>
              <div class="text-body-2 grey-7--text mt-1">未啟用前也可以先完成設定。</div>
            </v-card>

            <div v-for="rule in rules" :key="rule._key">
              <v-card
                v-if="activeRuleKey !== rule._key"
                outlined
                :color="isRulePendingDelete(rule) ? 'grey-2' : undefined"
                class="rounded-lg mb-3"
              >
                <v-list-item class="py-2">
                  <v-list-item-content>
                    <div class="mb-2">
                      <v-chip x-small outlined color="tiffany-8" class="mr-1">
                        {{ getRuleBadge(rule) }}
                      </v-chip>
                      <v-chip
                        x-small
                        outlined
                        :color="rule.action_type === 'open' ? 'green-8' : 'orange-6'"
                        class="mr-1"
                      >
                        {{ getRuleActionText(rule) }}
                      </v-chip>
                      <v-chip v-if="isRulePendingDelete(rule)" x-small color="grey-7" dark>
                        <v-icon x-small left>mdi-delete-outline</v-icon>
                        待刪除
                      </v-chip>
                      <v-chip v-else-if="isRuleStaged(rule)" x-small color="orange-6" dark>
                        尚未儲存
                      </v-chip>
                    </div>
                    <div :class="{ 'text-decoration-line-through': isRulePendingDelete(rule) }">
                      <v-list-item-title class="text-wrap font-weight-bold grey-5--text">
                        {{ getRuleTitle(rule) }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="d-flex align-center text-wrap mt-2">
                        <v-icon size="16" class="mr-1">mdi-clock-outline</v-icon>
                        {{ getRulePeriodText(rule) }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle v-if="rule.note" class="d-flex align-start text-wrap rule-note mt-1">
                        <v-icon size="16" class="mr-1">mdi-note-text-outline</v-icon>
                        {{ rule.note }}
                      </v-list-item-subtitle>
                    </div>
                  </v-list-item-content>
                  <v-list-item-action class="flex-row align-self-center">
                    <v-btn
                      v-if="isRulePendingDelete(rule)"
                      text
                      small
                      color="grey-7"
                      :disabled="Boolean(activeRuleKey)"
                      @click="undoDelete(rule)"
                    >
                      <v-icon small left>mdi-undo-variant</v-icon>
                      復原
                    </v-btn>
                    <template v-else>
                      <v-btn
                        icon
                        small
                        aria-label="編輯規則"
                        :disabled="Boolean(activeRuleKey)"
                        @click="beginEdit(rule)"
                      >
                        <v-icon size="19">mdi-pencil-outline</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        small
                        aria-label="刪除規則"
                        :disabled="Boolean(activeRuleKey)"
                        @click="askDelete(rule)"
                      >
                        <v-icon size="19">mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-list-item-action>
                </v-list-item>
              </v-card>
              <offwork-rule-editor
                v-else
                v-model="editingRule"
                class="mb-3"
                @cancel="cancelActiveEditor"
                @done="commitActiveEditor"
              />
            </div>

            <offwork-rule-editor
              v-if="editingIsNew"
              v-model="editingRule"
              is-new
              class="mb-3"
              @cancel="cancelActiveEditor"
              @done="commitActiveEditor"
            />

            <v-card v-if="!editingIsNew" outlined class="rounded-lg">
              <v-list-item
                link
                :disabled="Boolean(activeRuleKey)"
                class="tiffany-1"
                @click="beginAdd"
              >
                <v-list-item-avatar size="36" color="tiffany-2">
                  <v-icon color="tiffany-8">mdi-plus</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold tiffany-8--text">新增休診規則</v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    設定每天、每週、週期性或特定日期
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-icon>
                  <v-icon color="tiffany-8">mdi-chevron-right</v-icon>
                </v-list-item-icon>
              </v-list-item>
            </v-card>
          </v-card-text>
        </v-card>
      </div>
    </setting-layout>

    <v-snackbar
      :value="unsavedReminder"
      :timeout="-1"
      app
      top
      multi-line
      color="orange-6"
    >
      <div>
        <div class="font-weight-bold">變更尚未儲存</div>
        <div class="text-body-2">設定完成後，請按「確認儲存」。</div>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" text dark @click="save">立即儲存</v-btn>
      </template>
    </v-snackbar>

    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card class="rounded-lg pa-2">
        <v-card-title>刪除休診規則？</v-card-title>
        <v-card-text>
          「{{ deleteTarget ? getRuleTitle(deleteTarget) : '' }}」會先變成待刪除狀態，按「確認儲存」後才會正式刪除。
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey-7" @click="deleteDialog = false">取消</v-btn>
          <v-btn depressed color="red-6" dark @click="confirmDelete">待刪除</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  createOffworkAutoreplyRuleAPI,
  deleteOffworkAutoreplyRuleAPI,
  getOffworkAutoreplyAPI,
  updateOffworkAutoreplyRuleAPI
} from '@/api/clinic'
import {
  apiRuleToEditor,
  cloneOffworkValue,
  createEmptyOffworkRule,
  editorRuleToPayload,
  getRuleActionText,
  getRuleBadge,
  getRulePeriodText,
  getRuleTitle,
  isSameRulePayload,
  validateOffworkRule
} from '@/utils/offworkAutoreply'
import OffworkRuleEditor from './OffworkRuleEditor'
import SettingLayout from './SettingLayout'

export default {
  name: 'OffworkAutoreply',
  components: { OffworkRuleEditor, SettingLayout },
  inject: ['getIsEdit', 'toggleEdit', 'toggleLoading', 'setAlertData', 'showAlert'],
  props: {
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isEnabled: false,
      isOffworkNow: false,
      timezone: 'Asia/Taipei',
      rules: [],
      originalRules: [],
      deletedRuleIds: [],
      activeRuleKey: null,
      editingRule: null,
      editingIsNew: false,
      unsavedReminder: false,
      deleteDialog: false,
      deleteTarget: null,
      nextLocalKey: 1
    }
  },
  computed: {
    ...mapGetters(['axiosParamData']),
    statusTitle() {
      if (!this.isEnabled) return '尚未啟用'
      return this.isOffworkNow ? '已啟用，目前休診中' : '已啟用，目前營業中'
    }
  },
  methods: {
    getRuleBadge,
    getRuleTitle,
    getRuleActionText,
    getRulePeriodText,
    async loadData() {
      this.toggleLoading(true)
      try {
        const response = await getOffworkAutoreplyAPI(this.axiosParamData)
        const data = response.data || {}
        this.isEnabled = Boolean(data.is_enabled)
        this.isOffworkNow = Boolean(data.is_offwork_now)
        this.timezone = data.timezone || 'Asia/Taipei'
        this.rules = (data.rules || []).map(rule => apiRuleToEditor(rule, `server-${rule.id}`))
        this.originalRules = cloneOffworkValue(this.rules)
        this.deletedRuleIds = []
        this.unsavedReminder = false
        this.closeEditor()
      } catch (error) {
        this.notifyError(this.getErrorMessage(error, '休診設定載入失敗，請稍後再試'))
      } finally {
        this.toggleLoading(false)
      }
    },
    beginEdit(rule) {
      if (this.activeRuleKey) return
      this.editingRule = cloneOffworkValue(rule)
      this.activeRuleKey = rule._key
      this.editingIsNew = false
      this.toggleEdit(true)
    },
    beginAdd() {
      if (this.activeRuleKey) return
      const key = `local-${this.nextLocalKey}`
      this.nextLocalKey += 1
      this.editingRule = createEmptyOffworkRule(key)
      this.activeRuleKey = key
      this.editingIsNew = true
      this.toggleEdit(true)
    },
    commitActiveEditor() {
      const validationError = validateOffworkRule(this.editingRule)
      if (validationError) {
        this.notifyError(validationError)
        return false
      }

      const duplicateRule = this.rules.find(rule => (
        rule._key !== this.activeRuleKey &&
        !this.isRulePendingDelete(rule) &&
        isSameRulePayload(rule, this.editingRule)
      ))
      if (duplicateRule) {
        this.notifyError('已有完全相同的休診規則，請調整規則內容')
        return false
      }

      if (this.editingIsNew) {
        this.rules.push(cloneOffworkValue(this.editingRule))
      } else {
        const ruleIndex = this.rules.findIndex(rule => rule._key === this.activeRuleKey)
        if (ruleIndex >= 0) this.rules.splice(ruleIndex, 1, cloneOffworkValue(this.editingRule))
      }
      this.closeEditor()
      this.unsavedReminder = true
      return true
    },
    cancelActiveEditor() {
      this.closeEditor()
      if (!this.hasStagedChanges()) this.toggleEdit(false)
    },
    closeEditor() {
      this.activeRuleKey = null
      this.editingRule = null
      this.editingIsNew = false
    },
    askDelete(rule) {
      this.deleteTarget = rule
      this.deleteDialog = true
    },
    confirmDelete() {
      if (!this.deleteTarget) return
      if (this.deleteTarget.id) {
        if (!this.deletedRuleIds.includes(this.deleteTarget.id)) {
          this.deletedRuleIds.push(this.deleteTarget.id)
        }
      } else {
        const ruleIndex = this.rules.findIndex(rule => rule._key === this.deleteTarget._key)
        if (ruleIndex >= 0) this.rules.splice(ruleIndex, 1)
      }
      this.deleteDialog = false
      this.deleteTarget = null
      const hasChanges = this.hasStagedChanges()
      this.unsavedReminder = hasChanges
      this.toggleEdit(hasChanges)
    },
    undoDelete(rule) {
      this.deletedRuleIds = this.deletedRuleIds.filter(id => id !== rule.id)
      const hasChanges = this.hasStagedChanges()
      this.unsavedReminder = hasChanges
      this.toggleEdit(hasChanges)
    },
    cancel() {
      this.rules = cloneOffworkValue(this.originalRules)
      this.deletedRuleIds = []
      this.deleteDialog = false
      this.deleteTarget = null
      this.unsavedReminder = false
      this.closeEditor()
      this.toggleEdit(false)
    },
    async save() {
      if (this.editingRule && !this.commitActiveEditor()) return

      const activeRules = this.rules.filter(rule => !this.isRulePendingDelete(rule))
      const validationError = activeRules.map(validateOffworkRule).find(Boolean)
      if (validationError) {
        this.notifyError(validationError)
        return
      }

      if (!this.hasStagedChanges()) {
        this.unsavedReminder = false
        this.toggleEdit(false)
        return
      }

      this.toggleLoading(true)
      let appliedCount = 0
      try {
        for (const id of this.deletedRuleIds) {
          await deleteOffworkAutoreplyRuleAPI(id, this.axiosParamData)
          appliedCount += 1
        }

        const originalById = new Map(this.originalRules.filter(rule => rule.id).map(rule => [rule.id, rule]))
        for (const rule of activeRules) {
          const payload = editorRuleToPayload(rule)
          if (!rule.id) {
            await createOffworkAutoreplyRuleAPI(payload, this.axiosParamData)
            appliedCount += 1
          } else if (!isSameRulePayload(rule, originalById.get(rule.id))) {
            await updateOffworkAutoreplyRuleAPI(rule.id, payload, this.axiosParamData)
            appliedCount += 1
          }
        }

        await this.loadData()
        this.toggleEdit(false)
        this.setAlertData({
          icon: 'mdi-check-circle',
          color: 'primary',
          content: '休診設定儲存成功'
        })
        this.showAlert()
      } catch (error) {
        const message = this.getErrorMessage(error, '儲存失敗，請稍後再試')
        const status = error && error.response && error.response.status
        if (appliedCount > 0 || status === 404) {
          await this.loadData()
          this.toggleEdit(false)
          this.notifyError(`${message}；部分變更可能已生效，已重新載入最新設定`)
        } else {
          this.toggleEdit(true)
          this.notifyError(message)
        }
      } finally {
        this.toggleLoading(false)
      }
    },
    hasStagedChanges() {
      if (this.deletedRuleIds.length || this.rules.length !== this.originalRules.length) return true
      const originalById = new Map(this.originalRules.filter(rule => rule.id).map(rule => [rule.id, rule]))
      return this.rules.some(rule => !rule.id || !isSameRulePayload(rule, originalById.get(rule.id)))
    },
    isRuleStaged(rule) {
      if (!rule.id) return true
      const originalRule = this.originalRules.find(original => original.id === rule.id)
      return !originalRule || !isSameRulePayload(rule, originalRule)
    },
    isRulePendingDelete(rule) {
      return Boolean(rule.id && this.deletedRuleIds.includes(rule.id))
    },
    getErrorMessage(error, fallback) {
      const data = error && error.response && error.response.data
      if (!data) return fallback
      if (data.message) return data.message
      if (data.messages) return Array.isArray(data.messages) ? data.messages.join('、') : data.messages
      if (data.errors) {
        const firstErrors = Object.values(data.errors)[0]
        if (Array.isArray(firstErrors) && firstErrors[0]) return firstErrors[0]
      }
      return fallback
    },
    notifyError(message) {
      if (this.$notify && this.$notify.error) this.$notify.error(message)
    }
  },
  mounted() {
    this.loadData()
  }
}
</script>

<style scoped lang="scss">
.rule-note {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
</style>
